/**
 * IndexedDB store for offline time entry queuing.
 * Entries are stored locally when offline and synced when connection is restored.
 */

const DB_NAME = 'scheduler-contractor';
const DB_VERSION = 1;
const STORE_NAME = 'pending-time-entries';

export interface PendingTimeEntry {
  /** Client-generated UUID for idempotency */
  clientEntryId: string;
  taskId: string;
  action: 'start' | 'stop' | 'complete';
  deviceTimestamp: string;
  /** When this entry was queued locally */
  queuedAt: string;
  /** Number of sync attempts */
  attempts: number;
  /** Last error message if sync failed */
  lastError?: string;
}

let dbPromise: Promise<IDBDatabase> | null = null;

/**
 * Open or create the IndexedDB database.
 */
function openDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('Failed to open IndexedDB:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Create object store for pending time entries
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, {
          keyPath: 'clientEntryId',
        });
        // Index by taskId for querying entries per task
        store.createIndex('taskId', 'taskId', { unique: false });
        // Index by queuedAt for ordering
        store.createIndex('queuedAt', 'queuedAt', { unique: false });
      }
    };
  });

  return dbPromise;
}

/**
 * Add a pending time entry to the offline queue.
 */
export async function queueTimeEntry(
  entry: Omit<PendingTimeEntry, 'queuedAt' | 'attempts'>
): Promise<void> {
  const db = await openDB();

  const fullEntry: PendingTimeEntry = {
    ...entry,
    queuedAt: new Date().toISOString(),
    attempts: 0,
  };

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const request = store.add(fullEntry);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();
  });
}

/**
 * Get all pending time entries, ordered by queue time.
 */
export async function getPendingEntries(): Promise<PendingTimeEntry[]> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const index = store.index('queuedAt');
    const request = index.getAll();

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
  });
}

/**
 * Get pending entries for a specific task.
 */
export async function getPendingEntriesForTask(
  taskId: string
): Promise<PendingTimeEntry[]> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const index = store.index('taskId');
    const request = index.getAll(taskId);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
  });
}

/**
 * Remove a pending entry after successful sync.
 */
export async function removePendingEntry(
  clientEntryId: string
): Promise<void> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const request = store.delete(clientEntryId);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();
  });
}

/**
 * Update a pending entry (e.g., increment attempts, record error).
 */
export async function updatePendingEntry(
  clientEntryId: string,
  updates: Partial<Pick<PendingTimeEntry, 'attempts' | 'lastError'>>
): Promise<void> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const getRequest = store.get(clientEntryId);

    getRequest.onerror = () => reject(getRequest.error);
    getRequest.onsuccess = () => {
      const entry = getRequest.result as PendingTimeEntry | undefined;
      if (!entry) {
        resolve();
        return;
      }

      const updated = { ...entry, ...updates };
      const putRequest = store.put(updated);
      putRequest.onerror = () => reject(putRequest.error);
      putRequest.onsuccess = () => resolve();
    };
  });
}

/**
 * Get count of pending entries.
 */
export async function getPendingCount(): Promise<number> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const request = store.count();

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
  });
}

/**
 * Clear all pending entries (use with caution).
 */
export async function clearPendingEntries(): Promise<void> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const request = store.clear();

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();
  });
}
